import React from 'react';
import { useTheme } from '../context/ThemeContext';

const ThemeWrapper = ({ children, className = '', style = {}, ...props }) => {
  const { currentTheme, isDarkMode } = useTheme();

  const themeStyles = {
    backgroundColor: 'var(--color-background)',
    color: 'var(--color-text)',
    ...style
  };

  return (
    <div 
      className={`theme-wrapper ${className}`}
      style={themeStyles}
      data-theme={currentTheme}
      data-dark-mode={isDarkMode}
      {...props}
    >
      {children}
    </div>
  );
};

// Theme-aware input component
export const ThemeInput = ({ className = '', style = {}, ...props }) => {
  const inputStyles = {
    backgroundColor: 'var(--color-surface)',
    border: '2px solid var(--color-border)',
    color: 'var(--color-text)',
    ...style
  };

  return (
    <input
      className={`px-4 py-3 theme-input rounded-lg theme-focus ${className}`}
      style={inputStyles}
      {...props}
    />
  );
};

// Theme-aware select component
export const ThemeSelect = ({ className = '', style = {}, children, ...props }) => {
  const selectStyles = {
    backgroundColor: 'var(--color-surface)',
    border: '2px solid var(--color-border)',
    color: 'var(--color-text)',
    ...style
  };

  return (
    <select
      className={`px-4 py-3 theme-input rounded-lg theme-focus ${className}`}
      style={selectStyles}
      {...props}
    >
      {children}
    </select>
  );
};

// Theme-aware textarea component
export const ThemeTextarea = ({ className = '', style = {}, ...props }) => {
  const textareaStyles = {
    backgroundColor: 'var(--color-surface)',
    border: '2px solid var(--color-border)',
    color: 'var(--color-text)',
    minHeight: '100px',
    ...style
  };

  return (
    <textarea
      className={`px-4 py-3 theme-input rounded-lg theme-focus ${className}`}
      style={textareaStyles}
      {...props}
    />
  );
};

// Theme-aware button component
export const ThemeButton = ({ 
  variant = 'primary', 
  size = 'md', 
  className = '', 
  style = {}, 
  children, 
  ...props 
}) => {
  const getVariantStyles = () => {
    switch (variant) {
      case 'primary':
        return {
          backgroundColor: 'var(--color-primary)',
          color: 'white'
        };
      case 'secondary':
        return {
          backgroundColor: 'var(--color-secondary)',
          color: 'white'
        };
      case 'success':
        return {
          backgroundColor: 'var(--color-success)',
          color: 'white'
        };
      case 'warning':
        return {
          backgroundColor: 'var(--color-warning)',
          color: 'white'
        };
      case 'error':
        return {
          backgroundColor: 'var(--color-error)',
          color: 'white'
        };
      case 'outline':
        return {
          backgroundColor: 'transparent',
          color: 'var(--color-primary)',
          border: '2px solid var(--color-primary)'
        };
      default:
        return {
          backgroundColor: 'var(--color-primary)',
          color: 'white'
        };
    }
  };

  const getSizeStyles = () => {
    switch (size) {
      case 'sm':
        return { padding: '0.5rem 1rem', fontSize: '0.875rem' };
      case 'md':
        return { padding: '0.75rem 1.5rem', fontSize: '1rem' };
      case 'lg':
        return { padding: '1rem 2rem', fontSize: '1.125rem' };
      default:
        return { padding: '0.75rem 1.5rem', fontSize: '1rem' };
    }
  };

  const buttonStyles = {
    ...getVariantStyles(),
    ...getSizeStyles(),
    ...style
  };

  return (
    <button
      className={`rounded-lg font-medium transition-all duration-200 theme-hover-lift theme-focus ${className}`}
      style={buttonStyles}
      {...props}
    >
      {children}
    </button>
  );
};

// Theme-aware card component
export const ThemeCard = ({ className = '', style = {}, children, ...props }) => {
  const cardStyles = {
    backgroundColor: 'var(--color-surface)',
    border: '1px solid var(--color-border)',
    color: 'var(--color-text)',
    ...style
  };

  return (
    <div
      className={`profile-card theme-animate rounded-lg p-6 ${className}`}
      style={cardStyles}
      {...props}
    >
      {children}
    </div>
  );
};

// Theme-aware alert component
export const ThemeAlert = ({ type = 'info', className = '', style = {}, children, ...props }) => {
  const getAlertStyles = () => {
    switch (type) {
      case 'success':
        return {
          backgroundColor: 'var(--color-success-50)',
          border: '1px solid var(--color-success-200)',
          color: 'var(--color-success-700)'
        };
      case 'warning':
        return {
          backgroundColor: 'var(--color-warning-50)',
          border: '1px solid var(--color-warning-200)',
          color: 'var(--color-warning-700)'
        };
      case 'error':
        return {
          backgroundColor: 'var(--color-error-50)',
          border: '1px solid var(--color-error-200)',
          color: 'var(--color-error-700)'
        };
      default:
        return {
          backgroundColor: 'var(--color-primary-50)',
          border: '1px solid var(--color-primary-200)',
          color: 'var(--color-primary-700)'
        };
    }
  };

  const alertStyles = {
    ...getAlertStyles(),
    ...style
  };

  return (
    <div
      className={`p-4 rounded-lg ${className}`}
      style={alertStyles}
      {...props}
    >
      {children}
    </div>
  );
};

export default ThemeWrapper;
